/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { getHeaders } from "../../utility/getHeader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import logo from "../../assets/logo.png"
import { fetchComboData } from "../../services/api";
import {
    Card,
    Row,
    Col,
    Input,
    Select,
    DatePicker,
    Button,
    Table,
    Tag,
    Typography,
    Space,
    Statistic,
} from "antd";
import {
    FilterOutlined,
    ReloadOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    ArrowLeftOutlined,
    AppstoreOutlined,
    WalletOutlined,
    DollarOutlined,
} from "@ant-design/icons";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";

const { Text } = Typography;
const { Option } = Select;

export default function ItemWiseSummaryVat() {
    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [tableCategories, setTableCategories] = useState([]);
    const [selectedCatId, setSelectedCatId] = useState("");
    const [selectedSubCatId, setSelectedSubCatId] = useState("");
    const [filteredData, setFilteredData] = useState([]);

    const [formdata, setFormData] = useState({
        name: "",
    });
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Summary states
    const [showCategorySummary, setShowCategorySummary] = useState(false);
    const [showSubcategorySummary, setShowSubcategorySummary] = useState(false);
    const [showTableCategorySummary, setShowTableCategorySummary] = useState(false);
    const [categorySummaryData, setCategorySummaryData] = useState([]);
    const [subcategorySummaryData, setSubcategorySummaryData] = useState([]);
    const [tableCategorySummaryData, setTableCategorySummaryData] = useState([]);

    const moneyRender = (v) => `฿${v ? parseFloat(v).toFixed(2) : '0.00'}`;

    // Column definitions for VAT-specific display (antd Table format)
    const itemColumns = [
        {
            title: "Item Code",
            dataIndex: "item_code",
            key: "item_code",
            sorter: (a, b) => (a.item_code || '').localeCompare(b.item_code || ''),
            render: (v) => (v && v !== 'N/A' ? <Tag color="blue">{v}</Tag> : <Tag>N/A</Tag>),
        },
        {
            title: "Item Name",
            dataIndex: "item_name",
            key: "item_name",
            sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || ''),
            render: (v) => <Text strong>{v || '-'}</Text>,
        },
        { title: "Total Bills", dataIndex: "invoice_number", key: "invoice_number" },
        {
            title: "Last Sale Date",
            dataIndex: "setup_date",
            key: "setup_date",
            sorter: (a, b) => new Date(a.setup_date || 0) - new Date(b.setup_date || 0),
            render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '-'),
        },
        {
            title: "Quantity",
            dataIndex: "quantity",
            key: "quantity",
            sorter: (a, b) => parseFloat(a.quantity || 0) - parseFloat(b.quantity || 0),
        },
        { title: "UOM", dataIndex: "uom", key: "uom" },
        {
            title: "Rate",
            dataIndex: "rate",
            key: "rate",
            sorter: (a, b) => parseFloat(a.rate || 0) - parseFloat(b.rate || 0),
            render: moneyRender,
        },
        {
            title: "VAT %",
            dataIndex: "vat_rate",
            key: "vat_rate",
            render: (v) => <Tag color="gold">{v}%</Tag>,
        },
        {
            title: "VAT Amount",
            dataIndex: "vat_amount",
            key: "vat_amount",
            sorter: (a, b) => parseFloat(a.vat_amount || 0) - parseFloat(b.vat_amount || 0),
            render: moneyRender,
        },
        {
            title: "Total",
            dataIndex: "total_price",
            key: "total_price",
            sorter: (a, b) => parseFloat(a.total_price || 0) - parseFloat(b.total_price || 0),
            render: (v) => <Text strong style={{ color: '#0d9488' }}>{moneyRender(v)}</Text>,
        },
    ];

    const summaryColumns = (nameField, nameTitle) => [
        { title: nameTitle, dataIndex: nameField, key: nameField, render: (v) => <Text strong>{v}</Text> },
        {
            title: "Total Quantity",
            dataIndex: "total_quantity",
            key: "total_quantity",
            sorter: (a, b) => parseFloat(a.total_quantity || 0) - parseFloat(b.total_quantity || 0),
        },
        {
            title: "Total VAT",
            dataIndex: "total_vat",
            key: "total_vat",
            sorter: (a, b) => parseFloat(a.total_vat || 0) - parseFloat(b.total_vat || 0),
            render: moneyRender,
        },
        {
            title: "Total Amount",
            dataIndex: "total_amount",
            key: "total_amount",
            sorter: (a, b) => parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0),
            render: (v) => <Text strong style={{ color: '#0d9488' }}>{moneyRender(v)}</Text>,
        },
    ];

    const categorySummaryColumns = summaryColumns("category_name", "Category Name");
    const subcategorySummaryColumns = summaryColumns("subcategory_name", "Subcategory Name");
    const tableCategorySummaryColumns = summaryColumns("table_category_name", "Table Category Name");

    // Summary generation functions
    const generateCategorySummary = async () => {
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (selectedCatId) params.category_id = selectedCatId;

            const resData = await axios.get("/reports/category-wise-sales", {
                ...getHeaders(),
                params
            });

            const categories = resData.data?.data?.categories || [];

            const summaryData = categories.map(cat => {
                const totalQuantity = parseFloat(cat.total_quantity_sold || 0);
                const totalAmount = parseFloat(cat.total_sales_amount || 0);
                const vatRate = 7.00;
                const totalVat = totalAmount - (totalAmount / (1 + vatRate / 100));

                return {
                    category_id: cat.category_id,
                    category_name: cat.category_name || cat.catname || 'Uncategorized',
                    total_quantity: parseFloat(totalQuantity).toFixed(2),
                    total_vat: parseFloat(totalVat).toFixed(2),
                    total_amount: parseFloat(totalAmount).toFixed(2),
                    total_items: cat.total_items || 0,
                    total_bills: cat.total_bills || 0
                };
            });

            setCategorySummaryData(summaryData);
            setShowCategorySummary(true);
            setShowSubcategorySummary(false);
            setShowTableCategorySummary(false);
        } catch (error) {
            console.error("Error fetching category summary:", error);
            toast.error("Failed to load category summary");
        }
    };

    const generateSubcategorySummary = async () => {
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (selectedCatId) params.category_id = selectedCatId;
            if (selectedSubCatId) params.subcategory_id = selectedSubCatId;

            const resData = await axios.get("/reports/subcategory-wise-sales", {
                ...getHeaders(),
                params
            });

            const subcategories = resData.data?.data?.subcategories || [];

            const summaryData = subcategories.map(subcat => {
                const totalQuantity = parseFloat(subcat.total_quantity_sold || 0);
                const totalAmount = parseFloat(subcat.total_sales_amount || 0);
                const vatRate = 7.00;
                const totalVat = totalAmount - (totalAmount / (1 + vatRate / 100));

                return {
                    subcategory_id: subcat.subcategory_id,
                    subcategory_name: subcat.subcategory_name || subcat.subcatname || 'Uncategorized',
                    category_name: subcat.category_name || subcat.catname || '',
                    total_quantity: parseFloat(totalQuantity).toFixed(2),
                    total_vat: parseFloat(totalVat).toFixed(2),
                    total_amount: parseFloat(totalAmount).toFixed(2),
                    total_items: subcat.total_items || 0,
                    total_bills: subcat.total_bills || 0
                };
            });

            setSubcategorySummaryData(summaryData);
            setShowSubcategorySummary(true);
            setShowCategorySummary(false);
            setShowTableCategorySummary(false);
        } catch (error) {
            console.error("Error fetching subcategory summary:", error);
            toast.error("Failed to load subcategory summary");
        }
    };

    const generateTableCategorySummary = () => {
        const dataToSummarize = filteredData.length > 0 ? filteredData : data;
        const summaryMap = {};

        dataToSummarize.forEach(item => {
            const tableCategoryId = item.table_cat_id || item.table_category_id;
            if (!tableCategoryId) {
                return;
            }

            const tableCategory = tableCategories.find(tcat => tcat.id.toString() === tableCategoryId.toString());
            const tableCatName = tableCategory ? tableCategory.cat_name : `Unknown (ID: ${tableCategoryId})`;

            if (!summaryMap[tableCategoryId]) {
                summaryMap[tableCategoryId] = {
                    table_category_name: tableCatName,
                    total_quantity: 0,
                    total_vat: 0,
                    total_amount: 0
                };
            }

            const quantity = parseFloat(item.quantity || item.qty || 0);
            const vatAmount = parseFloat(item.vat_amount || item.tax_amount || 0);
            const totalAmount = parseFloat(item.total_price || item.total || item.total_amount || 0);

            summaryMap[tableCategoryId].total_quantity += quantity;
            summaryMap[tableCategoryId].total_vat += vatAmount;
            summaryMap[tableCategoryId].total_amount += totalAmount;
        });

        const summaryArray = Object.values(summaryMap);
        setTableCategorySummaryData(summaryArray);
        setShowTableCategorySummary(true);
        setShowCategorySummary(false);
        setShowSubcategorySummary(false);
    };

    // Filtering function - refetch data with filters applied
    const applyFilter = async () => {
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            toast.warn("Invalid date range: Start date is after end date");
            return;
        }

        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const resData = await axios.get("/reports/item-wise-sales", {
                ...getHeaders(),
                params
            });

            const items = resData.data?.data?.items || [];

            let processedData = items.map(item => {
                const quantity = parseFloat(item.total_quantity_sold || 0);
                const totalPrice = parseFloat(item.total_sales_amount || 0);
                const avgPrice = parseFloat(item.average_selling_price || 0);
                const vatRate = 7.00;
                const calculatedVatAmount = totalPrice - (totalPrice / (1 + vatRate / 100));
                const calculatedRate = avgPrice > 0 ? avgPrice : (quantity > 0 ? (totalPrice / (1 + vatRate / 100)) / quantity : 0);

                return {
                    item_id: item.item_id,
                    item_code: item.item_code || 'N/A',
                    item_name: item.item_name || 'Unknown',
                    invoice_number: (item.total_bills || 0) + ' Bills',
                    total_bills: item.total_bills || 0,
                    setup_date: item.last_sale_date || item.first_sale_date || '',
                    quantity: parseFloat(quantity).toFixed(2),
                    uom: item.unit || 'PCS',
                    rate: parseFloat(calculatedRate).toFixed(2),
                    vat_rate: parseFloat(vatRate).toFixed(2),
                    vat_amount: parseFloat(calculatedVatAmount).toFixed(2),
                    total_price: parseFloat(totalPrice).toFixed(2),
                    category_name: item.category_name || 'No Category',
                    subcategory_name: item.subcategory_name || 'No Subcategory',
                    catid: null,
                    subcatid: null,
                    first_sale_date: item.first_sale_date,
                    last_sale_date: item.last_sale_date
                };
            });

            if (formdata.name) {
                processedData = processedData.filter(item =>
                    item.item_name?.toLowerCase().includes(formdata.name.toLowerCase())
                );
            }

            if (selectedCatId) {
                const category = categories.find(cat => cat.id.toString() === selectedCatId.toString());
                if (category) {
                    processedData = processedData.filter(item =>
                        item.category_name === category.name
                    );
                }
            }

            if (selectedSubCatId) {
                const subcategory = subcategories.find(sub => sub.id.toString() === selectedSubCatId.toString());
                if (subcategory) {
                    processedData = processedData.filter(item =>
                        item.subcategory_name === subcategory.subcat
                    );
                }
            }

            setFilteredData(processedData);
            toast.success("Filter applied successfully");
        } catch (error) {
            console.error("Error applying filter:", error);
            toast.error("Failed to apply filter");
        }
    };

    // Clear filters
    const clearFilters = () => {
        setStartDate("");
        setEndDate("");
        setFormData({ name: "" });
        setSelectedCatId("");
        setSelectedSubCatId("");
        setFilteredData(data);
        setShowCategorySummary(false);
        setShowSubcategorySummary(false);
        setShowTableCategorySummary(false);
        setCategorySummaryData([]);
        setSubcategorySummaryData([]);
        setTableCategorySummaryData([]);
    };

    // Export to PDF
    const exportPDF = () => {
        const doc = new jsPDF();
        const exportData = filteredData.length > 0 ? filteredData : data;

        doc.addImage(logo, "PNG", 150, 10, 40, 15);

        doc.setFontSize(16);
        doc.text("Item Wise VAT Summary", 14, 20);

        const tableColumn = [
            "Invoice No", "Date", "Item", "Qty", "UOM", "Rate",
            "VAT %", "VAT Amount", "Total"
        ];

        const tableRows = [];
        let vatTotal = 0;
        let grandTotal = 0;

        exportData.forEach((item) => {
            const invoiceNo = item.invoice_number || item.invoice_no || item.bill_no;
            const date = item.setup_date;
            const itemName = item.item_name || item.name || item.product_name;
            const quantity = item.quantity || item.qty;
            const rate = item.rate || item.price || item.unit_price;
            const vatRate = item.vat_rate || item.cgst || item.tax_rate;
            const vatAmount = item.vat_amount || item.tax_amount;
            const totalPrice = item.total_price || item.total || item.total_amount;

            tableRows.push([
                invoiceNo,
                date ? format(new Date(date), "yyyy-MM-dd") : "",
                itemName,
                quantity,
                item.uom || item.unit,
                rate,
                vatRate,
                vatAmount,
                totalPrice,
            ]);

            vatTotal += parseFloat(vatAmount || 0);
            grandTotal += parseFloat(totalPrice || 0);
        });

        doc.autoTable({
            startY: 30,
            head: [tableColumn],
            body: tableRows,
        });

        const finalY = doc.autoTable.previous.finalY + 10;

        doc.setFontSize(12);
        doc.text("Summary", 14, finalY);
        doc.text(`Total VAT: ${vatTotal.toFixed(2)}`, 14, finalY + 8);
        doc.text(`Total Amount: ${grandTotal.toFixed(2)}`, 14, finalY + 16);

        doc.save("itemwise_vat_summary.pdf");
    };

    // Export to Excel
    const exportExcel = () => {
        const exportData = filteredData.length > 0 ? filteredData : data;

        const worksheetData = exportData.map(item => {
            const invoiceNo = item.invoice_number || item.invoice_no || item.bill_no;
            const date = item.setup_date;
            const itemName = item.item_name || item.name || item.product_name;
            const categoryName = item.category_name || item.cat_name;
            const subcategoryName = item.subcategory_name || item.subcat_name || item.sub_category;
            const tableCategoryName = item.table_category_name || item.table_cat_name;
            const quantity = item.quantity || item.qty;
            const rate = item.rate || item.price || item.unit_price;
            const vatRate = item.vat_rate || item.cgst || item.tax_rate;
            const vatAmount = item.vat_amount || item.tax_amount;
            const totalPrice = item.total_price || item.total || item.total_amount;

            return {
                "Invoice No": invoiceNo,
                "Date": date ? format(new Date(date), "yyyy-MM-dd") : "",
                "Item Code": item.item_code,
                "Category": categoryName,
                "Subcategory": subcategoryName,
                "Table Category": tableCategoryName,
                "Item Name": itemName,
                "Quantity": quantity,
                "UOM": item.uom || item.unit,
                "Rate": rate,
                "VAT %": vatRate,
                "VAT Amount": vatAmount,
                "Total": totalPrice
            };
        });

        const vatTotal = exportData.reduce((acc, item) => {
            const vatAmount = item.vat_amount || item.tax_amount;
            return acc + parseFloat(vatAmount || 0);
        }, 0);

        const grandTotal = exportData.reduce((acc, item) => {
            const totalPrice = item.total_price || item.total || item.total_amount;
            return acc + parseFloat(totalPrice || 0);
        }, 0);

        worksheetData.push({});
        worksheetData.push({
            "Invoice No": "TOTALS",
            "VAT Amount": vatTotal.toFixed(2),
            "Total": grandTotal.toFixed(2)
        });

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "VAT Summary");

        XLSX.writeFile(workbook, "itemwise_vat_summary.xlsx");
    };

    // Data fetching useEffects
    useEffect(() => {
        const fetchDataAndCategories = async () => {
            try {
                const params = {};
                if (startDate) params.start_date = startDate;
                if (endDate) params.end_date = endDate;

                const resData = await axios.get("/reports/item-wise-sales", {
                    ...getHeaders(),
                    params
                });

                const items = resData.data?.data?.items || [];

                const processedData = items.map(item => {
                    const quantity = parseFloat(item.total_quantity_sold || 0);
                    const totalPrice = parseFloat(item.total_sales_amount || 0);
                    const avgPrice = parseFloat(item.average_selling_price || 0);

                    const vatRate = 7.00;

                    const calculatedVatAmount = totalPrice - (totalPrice / (1 + vatRate / 100));

                    const calculatedRate = avgPrice > 0 ? avgPrice : (quantity > 0 ? (totalPrice / (1 + vatRate / 100)) / quantity : 0);

                    return {
                        item_id: item.item_id,
                        item_code: item.item_code || 'N/A',
                        item_name: item.item_name || 'Unknown',
                        invoice_number: (item.total_bills || 0) + ' Bills',
                        total_bills: item.total_bills || 0,
                        setup_date: item.last_sale_date || item.first_sale_date || '',
                        quantity: parseFloat(quantity).toFixed(2),
                        uom: item.unit || 'PCS',
                        rate: parseFloat(calculatedRate).toFixed(2),
                        vat_rate: parseFloat(vatRate).toFixed(2),
                        vat_amount: parseFloat(calculatedVatAmount).toFixed(2),
                        total_price: parseFloat(totalPrice).toFixed(2),
                        category_name: item.category_name || 'No Category',
                        subcategory_name: item.subcategory_name || 'No Subcategory',
                        catid: null,
                        subcatid: null,
                        first_sale_date: item.first_sale_date,
                        last_sale_date: item.last_sale_date
                    };
                });

                setData(processedData);
                setFilteredData(processedData);
            } catch (error) {
                console.error("Error fetching item-wise sales report:", error);
                toast.error("Failed to load item-wise sales report");
            }
        };
        fetchDataAndCategories();
    }, [startDate, endDate]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const cats = await fetchComboData("categories", "name");
                setCategories(cats);
            } catch (err) {
                console.error("Error fetching categories:", err);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fetchTableCategories = async () => {
            try {
                const tableCats = await fetchComboData("table_category", "cat_name");
                setTableCategories(tableCats);
            } catch (err) {
                console.error("Error fetching table categories:", err);
            }
        };
        fetchTableCategories();
    }, []);

    useEffect(() => {
        const fetchSubcategories = async () => {
            if (!selectedCatId) {
                setSubcategories([]);
                setSelectedSubCatId("");
                return;
            }
            try {
                const subs = await fetchComboData("subcategory", "subcat", { catid: selectedCatId });
                setSubcategories(subs);
            } catch (err) {
                console.error("Error fetching subcategories:", err);
            }
        };
        fetchSubcategories();
    }, [selectedCatId]);

    const displayData = showCategorySummary
        ? categorySummaryData
        : showSubcategorySummary
        ? subcategorySummaryData
        : showTableCategorySummary
        ? tableCategorySummaryData
        : (filteredData.length > 0 ? filteredData : data);

    const displayColumns = showCategorySummary
        ? categorySummaryColumns
        : showSubcategorySummary
        ? subcategorySummaryColumns
        : showTableCategorySummary
        ? tableCategorySummaryColumns
        : itemColumns;

    const totalVat = displayData.reduce((acc, item) => {
        const vatAmount = item.vat_amount ?? item.tax_amount ?? item.total_vat;
        return acc + parseFloat(vatAmount || 0);
    }, 0);

    const grandTotal = displayData.reduce((acc, item) => {
        const totalPrice = item.total_price ?? item.total ?? item.total_amount;
        return acc + parseFloat(totalPrice || 0);
    }, 0);

    return (
        <>
            <Layout>
                <Header title="Item Wise VAT Summary" />
                <ToastContainer />

                <Card
                    style={{ marginBottom: 16, borderRadius: 12, background: '#f0fdfa', border: '1px solid #99f6e4' }}
                    styles={{ body: { padding: 16 } }}
                >
                    <Row gutter={[16, 12]}>
                        <Col xs={24} sm={12} md={8}>
                            <Text style={{ fontSize: 13 }}>Start Date</Text>
                            <DatePicker
                                style={{ width: '100%' }}
                                value={startDate ? dayjs(startDate) : null}
                                onChange={(_, dateString) => setStartDate(dateString || "")}
                                format="DD-MM-YYYY"
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Text style={{ fontSize: 13 }}>End Date</Text>
                            <DatePicker
                                style={{ width: '100%' }}
                                value={endDate ? dayjs(endDate) : null}
                                onChange={(_, dateString) => setEndDate(dateString || "")}
                                format="DD-MM-YYYY"
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Text style={{ fontSize: 13 }}>Item Name</Text>
                            <Input
                                placeholder="Enter item name"
                                value={formdata.name}
                                onChange={(e) => setFormData({ ...formdata, name: e.target.value })}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Text style={{ fontSize: 13 }}>Category</Text>
                            <Select
                                style={{ width: '100%' }}
                                value={selectedCatId || undefined}
                                placeholder="All Categories"
                                allowClear
                                onChange={(value) => setSelectedCatId(value || "")}
                            >
                                {categories.map(cat => (
                                    <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Text style={{ fontSize: 13 }}>Subcategory</Text>
                            <Select
                                style={{ width: '100%' }}
                                value={selectedSubCatId || undefined}
                                placeholder="All Subcategories"
                                allowClear
                                onChange={(value) => setSelectedSubCatId(value || "")}
                            >
                                {subcategories.map(sub => (
                                    <Option key={sub.id} value={sub.id}>{sub.subcat}</Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>

                    <Row style={{ marginTop: 16 }}>
                        <Col xs={24}>
                            <Space wrap>
                                <Button type="primary" icon={<FilterOutlined />} onClick={applyFilter}>
                                    Apply Filter
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={clearFilters}>
                                    Clear Filters
                                </Button>
                                <Button icon={<AppstoreOutlined />} onClick={generateCategorySummary}>
                                    Category Summary
                                </Button>
                                <Button icon={<AppstoreOutlined />} onClick={generateSubcategorySummary}>
                                    Subcategory Summary
                                </Button>
                                <Button icon={<AppstoreOutlined />} onClick={generateTableCategorySummary}>
                                    Table Category Summary
                                </Button>
                                <Button danger icon={<FilePdfOutlined />} onClick={exportPDF}>
                                    Export PDF
                                </Button>
                                <Button icon={<FileExcelOutlined />} onClick={exportExcel} style={{ color: '#16a34a', borderColor: '#86efac' }}>
                                    Export Excel
                                </Button>
                                {(showCategorySummary || showSubcategorySummary || showTableCategorySummary) && (
                                    <Button
                                        icon={<ArrowLeftOutlined />}
                                        onClick={() => {
                                            setShowCategorySummary(false);
                                            setShowSubcategorySummary(false);
                                            setShowTableCategorySummary(false);
                                        }}
                                    >
                                        Back to Item Details
                                    </Button>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </Card>

                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={8}>
                        <Card style={{ borderRadius: 12, background: '#eff6ff', border: '1px solid #bfdbfe' }} styles={{ body: { padding: 16 } }}>
                            <Statistic
                                title="Total Records"
                                value={displayData.length}
                                prefix={<AppstoreOutlined style={{ color: '#2563eb' }} />}
                                valueStyle={{ color: '#1d4ed8', fontSize: 22 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card style={{ borderRadius: 12, background: '#fffbeb', border: '1px solid #fde68a' }} styles={{ body: { padding: 16 } }}>
                            <Statistic
                                title="Total VAT"
                                value={totalVat}
                                precision={2}
                                prefix={<WalletOutlined style={{ color: '#d97706' }} />}
                                suffix="฿"
                                valueStyle={{ color: '#b45309', fontSize: 22 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card style={{ borderRadius: 12, background: '#f0fdf4', border: '1px solid #bbf7d0' }} styles={{ body: { padding: 16 } }}>
                            <Statistic
                                title="Grand Total"
                                value={grandTotal}
                                precision={2}
                                prefix={<DollarOutlined style={{ color: '#16a34a' }} />}
                                suffix="฿"
                                valueStyle={{ color: '#15803d', fontSize: 22 }}
                            />
                        </Card>
                    </Col>
                </Row>

                <Card style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
                    <Table
                        rowKey={(record, index) => record.item_id ?? record.category_id ?? record.subcategory_id ?? index}
                        columns={displayColumns}
                        dataSource={displayData}
                        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 25, 50, 100] }}
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: "No data available" }}
                    />
                </Card>
            </Layout>
        </>
    );
}
